const COLLECTION_SORTS = new Set(['created-descending', 'created-ascending', 'price-ascending', 'price-descending', 'title-ascending', 'title-descending', 'best-selling', 'manual']);
const SEARCH_SORTS = new Set(['relevance', 'price-ascending', 'price-descending']);
const PREVIEW_PARAMS = ['preview_theme_id', 'preview_token', '_cd', '_uid', 'pb'];

function normalizePrice(value) {
  const text = String(value ?? '').trim();
  return /^\d+(?:\.\d{1,2})?$/.test(text) && Number(text) <= 1000000000 ? String(Number(text)) : '';
}

export function normalizeState(value) {
  const q = String(value.q || '').trim().slice(0, 200);
  const collection = /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(value.collection || '') ? value.collection : 'all-products';
  const sorts = q ? SEARCH_SORTS : COLLECTION_SORTS;
  const sort = sorts.has(value.sort) ? value.sort : q ? 'relevance' : 'created-descending';
  const page = /^\d+$/.test(String(value.page)) ? Math.max(1, Math.min(1000, Number(value.page))) : 1;
  let priceMin = normalizePrice(value.priceMin);
  let priceMax = normalizePrice(value.priceMax);
  if (priceMin !== '' && priceMax !== '' && Number(priceMin) > Number(priceMax)) [priceMin, priceMax] = [priceMax, priceMin];
  return { collection: q ? 'all-products' : collection, q, sort, page, ...(priceMin !== '' ? { priceMin } : {}), ...(priceMax !== '' ? { priceMax } : {}) };
}

export function parseState(input) {
  const params = new URL(input).searchParams;
  return normalizeState({ collection: params.get('collection'), q: params.get('q'), sort: params.get('sort_by'), page: params.get('page') || 1, priceMin: params.get('filter.v.price.gte'), priceMax: params.get('filter.v.price.lte') });
}

export function catalogRobots(value) {
  const state = normalizeState(value);
  return state.q || state.priceMin !== undefined || state.priceMax !== undefined || state.collection !== 'all-products' || state.sort !== 'created-descending' ? 'noindex,follow' : 'index,follow';
}

export function nextState(current, patch) {
  const value = { ...current, ...patch };
  if ('collection' in patch) value.q = '';
  if ('q' in patch) value.collection = 'all-products';
  if ('collection' in patch || 'q' in patch || 'sort' in patch || 'priceMin' in patch || 'priceMax' in patch) value.page = 1;
  return normalizeState(value);
}

export function endpointFor(value, input, routes, fragment = true) {
  const state = normalizeState(value);
  const base = new URL(input);
  const endpoint = new URL(state.q ? routes.search : routes.collections.replace(/\/$/, '') + '/' + state.collection, base);
  if (endpoint.origin !== base.origin) throw new Error('Catalogue routes must be same-origin');
  endpoint.search = '';
  if (fragment) endpoint.searchParams.set('view', 'eme-catalog-data');
  if (state.q) {
    endpoint.searchParams.set('q', state.q);
    endpoint.searchParams.set('type', 'product');
    endpoint.searchParams.set('options[unavailable_products]', 'show');
    endpoint.searchParams.set('options[prefix]', 'last');
  }
  endpoint.searchParams.set('sort_by', state.sort);
  endpoint.searchParams.set('page', String(state.page));
  if (state.priceMin !== undefined) endpoint.searchParams.set('filter.v.price.gte', state.priceMin);
  if (state.priceMax !== undefined) endpoint.searchParams.set('filter.v.price.lte', state.priceMax);
  for (const key of PREVIEW_PARAMS) {
    if (base.searchParams.has(key)) endpoint.searchParams.set(key, base.searchParams.get(key));
  }
  return endpoint;
}

export function urlForState(value, input) {
  const state = normalizeState(value);
  const url = new URL(input);
  for (const key of ['collection', 'q', 'sort_by', 'page', 'filter.v.price.gte', 'filter.v.price.lte']) url.searchParams.delete(key);
  if (state.q) url.searchParams.set('q', state.q);
  else if (state.collection !== 'all-products') url.searchParams.set('collection', state.collection);
  url.searchParams.set('sort_by', state.sort);
  if (state.page > 1) url.searchParams.set('page', String(state.page));
  if (state.priceMin !== undefined) url.searchParams.set('filter.v.price.gte', state.priceMin);
  if (state.priceMax !== undefined) url.searchParams.set('filter.v.price.lte', state.priceMax);
  url.hash = '';
  return url;
}

if (typeof window !== 'undefined' && !customElements.get('eme-product-catalog')) {
  customElements.define('eme-product-catalog', class extends HTMLElement {
    get activeState() {
      return this.loading && this.pendingState ? this.pendingState : this.state;
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.routes = { collections: this.dataset.collectionsRoute, search: this.dataset.searchRoute };
      this.searchInput = this.querySelector('[data-catalog-query]');
      this.results = this.querySelector('[data-catalog-results]');
      this.status = this.querySelector('[data-catalog-status]');
      this.error = this.querySelector('[data-catalog-error]');
      this.onClick = event => {
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
        const category = event.target.closest('[data-catalog-collection]');
        const page = event.target.closest('[data-catalog-page]');
        const clear = event.target.closest('[data-catalog-clear]');
        const retry = event.target.closest('[data-catalog-retry]');
        const clearPrice = event.target.closest('[data-catalog-clear-price]');
        if (clearPrice) {
          event.preventDefault();
          this.load(nextState(this.activeState, { priceMin: '', priceMax: '' }), true);
        } else if (category) {
          event.preventDefault();
          this.load(nextState(this.activeState, { collection: category.dataset.catalogCollection }), true);
        } else if (page) {
          event.preventDefault();
          this.load(nextState(this.activeState, { page: page.dataset.catalogPage }), true, true);
        } else if (clear) {
          event.preventDefault();
          this.load(nextState(this.activeState, { q: '' }), true);
        } else if (retry) {
          event.preventDefault();
          this.load(this.pendingState || this.state, true);
        }
      };
      this.onSubmit = event => {
        if (event.target.matches('[data-catalog-price-form]')) {
          event.preventDefault();
          const min = this.querySelector('[data-catalog-price-min]');
          const max = this.querySelector('[data-catalog-price-max]');
          max.setCustomValidity('');
          if (min.value !== '' && max.value !== '' && Number(min.value) > Number(max.value)) max.setCustomValidity(event.target.dataset.rangeError);
          if (!event.target.reportValidity()) return;
          this.load(nextState(this.activeState, { priceMin: min.value, priceMax: max.value }), true);
        } else if (event.target.matches('[data-catalog-search]')) {
          event.preventDefault();
          this.load(nextState(this.activeState, { q: this.searchInput.value }), true);
        } else if (event.target.matches('[data-catalog-sort-form]')) {
          event.preventDefault();
          this.load(nextState(this.activeState, { sort: event.target.querySelector('[data-catalog-sort]').value }), true);
        }
      };
      this.onChange = event => {
        if (event.target.matches('[data-catalog-sort]')) this.load(nextState(this.activeState, { sort: event.target.value }), true);
      };
      this.onPop = () => this.load(parseState(window.location.href));
      this.onInput = event => {
        if (event.target.matches('[data-catalog-price-min], [data-catalog-price-max]')) this.querySelector('[data-catalog-price-max]')?.setCustomValidity('');
      };
      this.addEventListener('click', this.onClick);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      window.addEventListener('popstate', this.onPop);
      this.state = parseState(window.location.href);
      const allowed = new Set([...this.querySelectorAll('[data-catalog-collection]')].map(node => node.dataset.catalogCollection));
      if (!allowed.has(this.state.collection)) this.state = normalizeState({ ...this.state, collection: 'all-products' });
      this.searchInput.value = this.state.q;
      this.updateIndexing(this.state);
      if (window.matchMedia('(max-width: 989px)').matches) this.querySelector('.eme-catalog__navigation').removeAttribute('open');
      if (this.state.q || this.state.priceMin !== undefined || this.state.priceMax !== undefined || this.state.collection !== 'all-products' || this.state.sort !== 'created-descending' || this.state.page > 1) this.load(this.state);
      else this.syncControls();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.removeEventListener('click', this.onClick);
      this.removeEventListener('submit', this.onSubmit);
      this.removeEventListener('change', this.onChange);
      this.removeEventListener('input', this.onInput);
      window.removeEventListener('popstate', this.onPop);
      this.initialized = false;
    }

    updateIndexing(state) {
      const robots = this.ownerDocument?.head?.querySelector('[data-eme-catalog-robots]');
      if (robots) robots.setAttribute('content', catalogRobots(state));
    }

    syncControls() {
      this.searchInput.value = this.state.q;
      const min = this.querySelector('[data-catalog-price-min]');
      const max = this.querySelector('[data-catalog-price-max]');
      if (min) min.value = this.state.priceMin ?? '';
      if (max) { max.value = this.state.priceMax ?? ''; max.setCustomValidity(''); }
      const clearPrice = this.querySelector('[data-catalog-clear-price]');
      if (clearPrice) clearPrice.hidden = this.state.priceMin === undefined && this.state.priceMax === undefined;
      for (const node of this.querySelectorAll('[data-catalog-collection]')) {
        const active = !this.state.q && node.dataset.catalogCollection === this.state.collection;
        node.classList.toggle('is-active', active);
        if (active) node.setAttribute('aria-current', 'true');
        else node.removeAttribute('aria-current');
      }
      const sort = this.querySelector('[data-catalog-sort]');
      if (sort && [...sort.options].some(option => option.value === this.state.sort)) sort.value = this.state.sort;
      const clear = this.querySelector('[data-catalog-clear]');
      if (clear) clear.hidden = !this.state.q;
    }

    async load(value, push = false, scroll = false) {
      const desired = normalizeState(value);
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.pendingState = desired;
      this.loading = true;
      this.results.setAttribute('aria-busy', 'true');
      this.status.textContent = this.dataset.loadingText;
      this.error.hidden = true;
      try {
        const response = await fetch(endpointFor(desired, window.location.href, this.routes), { signal: controller.signal, credentials: 'same-origin', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('Catalogue HTTP ' + response.status);
        const html = await response.text();
        if (controller.signal.aborted || this.controller !== controller) return;
        const document = new DOMParser().parseFromString(html, 'text/html');
        const fragment = document.querySelector('[data-catalog-response]');
        if (!fragment) throw new Error('Missing catalogue fragment');
        const actualPage = Number(fragment.dataset.currentPage) || desired.page;
        const selectedSort = fragment.querySelector('[data-catalog-sort]')?.value || desired.sort;
        this.state = normalizeState({ ...desired, page: actualPage, sort: selectedSort });
        this.results.replaceChildren(fragment);
        if (push) history.pushState({}, '', urlForState(this.state, window.location.href));
        this.updateIndexing(this.state);
        this.syncControls();
        this.status.textContent = fragment.querySelector('[data-catalog-count]')?.textContent || '';
        this.pendingState = null;
        if (scroll) {
          this.results.scrollIntoView({ block: 'start', behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
          this.results.focus({ preventScroll: true });
        }
      } catch (error) {
        if (controller.signal.aborted || this.controller !== controller) return;
        this.status.textContent = this.dataset.errorText;
        this.error.hidden = false;
        this.error.querySelector('[data-catalog-fallback]').href = endpointFor(desired, window.location.href, this.routes, false).href;
        this.syncControls();
      } finally {
        if (this.controller === controller) {
          this.loading = false;
          this.results.setAttribute('aria-busy', 'false');
        }
      }
    }
  });
}


